import { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, AuthContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, Shield } from "lucide-react";

const AdminLogin = () => {
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/login`, formData);
      
      if (response.data.user.role !== "admin") {
        toast.error("Access denied. Admin credentials required.");
        setLoading(false);
        return;
      }
      
      login(response.data.token, response.data.user);
      toast.success("Admin login successful!");
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0E366A] via-[#1a4d7a] to-[#0E366A] flex flex-col">
      <div className="bg-[#F7B538] py-2 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <div className="text-sm text-[#0E366A] font-medium">London Islamic School - Admin Portal</div>
        </div>
      </div>
      
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <Button 
            data-testid="back-btn"
            variant="ghost" 
            onClick={() => navigate('/')}
            className="mb-4 text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
          
          <div className="text-center mb-8">
            <img src="/lis_logo.png" alt="London Islamic School Logo" className="mx-auto mb-4 h-32 w-auto"/>
            <h1 className="text-3xl font-serif text-white mb-2">Admin Portal</h1>
            <p className="text-[#F7B538] text-sm italic">London Islamic School</p>
          </div>

          <Card className="shadow-2xl border-0 bg-white/95 backdrop-blur">
            <CardHeader className="space-y-1 bg-[#0E366A] text-white rounded-t-lg">
              <CardTitle className="text-2xl font-bold text-center">Admin Login</CardTitle>
              <CardDescription className="text-center text-white/90">Access the school management system</CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input 
                  data-testid="admin-email-input"
                  id="email" 
                  name="email" 
                  type="email" 
                  required 
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input 
                  data-testid="admin-password-input"
                  id="password" 
                  name="password" 
                  type="password" 
                  required 
                  value={formData.password}
                  onChange={handleChange}
                />
              </div>
              
              <Button 
                data-testid="admin-login-submit-btn"
                type="submit" 
                className="w-full bg-[#F7B538] hover:bg-[#e5a627] text-[#0E366A] font-semibold" 
                disabled={loading}
              >
                {loading ? "Logging in..." : "Login as Admin"}
              </Button>
            </form>
          </CardContent>
        </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;